import { useState, useEffect, useCallback } from "react";
import {
  apiFetch,
  getStreamToken,
  jobEventsUrl,
  type JobInfo,
  type StreamEvent,
} from "@/lib/api";
import { useJobPolling } from "@/hooks/useJobPolling";

/** A single streamed preview frame, decoded into a usable `data:` URI. */
export interface JobPartial {
  index: number;
  dataUri: string;
}

export interface JobEventsState {
  /**
   * Canonical job record. Seeded via GET on connect, refetched on the terminal
   * (completed/failed) event, or driven entirely by the polling fallback.
   */
  job: JobInfo | null;
  /** Latest progress percentage (0–100) reported by the stream, if any. */
  pct?: number;
  /** Latest human-readable progress message reported by the stream, if any. */
  message?: string;
  /** Accumulated partial previews, in arrival order (use the last for display). */
  partials: JobPartial[];
  /** True while a live SSE connection is the source; false on fallback/idle/done. */
  streaming: boolean;
  /** Stream-reported failure text (mirrors job.error.message after refetch). */
  error: string | null;
}

/**
 * Live job tracking over Server-Sent Events with a graceful polling fallback.
 *
 * Flow: mint a short-lived stream token → open an EventSource → translate
 * `progress`/`partial` frames into local state for an in-progress preview. On
 * `completed`/`failed` we refetch GET /api/jobs/{id} (final assets + presign-able
 * keys) and close the stream — so `job` ends up identical to what plain polling
 * would yield, keeping callers' terminal-state logic unchanged.
 *
 * If the token request fails, EventSource is unsupported, or the connection dies
 * before a terminal event, we transparently degrade to {@link useJobPolling}
 * (2s polling). Callers can read `streaming` to know which path is active.
 */
export function useJobEvents(jobId: string | null): JobEventsState {
  const [job, setJob] = useState<JobInfo | null>(null);
  const [pct, setPct] = useState<number | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [partials, setPartials] = useState<JobPartial[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  // Engaged only when SSE is unavailable or drops mid-flight.
  const [fallback, setFallback] = useState(false);

  // Dormant (null id) until we explicitly fall back, so the two never overlap.
  const { job: polledJob, error: pollError } = useJobPolling(fallback ? jobId : null);

  const refetchJob = useCallback(async (): Promise<JobInfo | null> => {
    if (!jobId) return null;
    try {
      const fresh = await apiFetch<JobInfo>(`/api/jobs/${jobId}`);
      setJob(fresh);
      return fresh;
    } catch {
      // Non-fatal: the live overlay still renders from stream events.
      return null;
    }
  }, [jobId]);

  useEffect(() => {
    // Reset all per-job state on every switch.
    setJob(null);
    setPct(undefined);
    setMessage(undefined);
    setPartials([]);
    setError(null);
    setStreaming(false);
    setFallback(false);

    if (!jobId) return;

    // No EventSource (legacy/SSR) → straight to polling.
    if (typeof EventSource === "undefined") {
      setFallback(true);
      return;
    }

    let active = true;
    let es: EventSource | null = null;

    const degrade = () => {
      if (!active) return;
      es?.close();
      es = null;
      setStreaming(false);
      setFallback(true);
    };

    // Seed the card immediately (fire-and-forget) so it appears while we connect.
    void refetchJob();

    (async () => {
      let token: string;
      try {
        token = (await getStreamToken(jobId)).token;
      } catch {
        degrade();
        return;
      }
      if (!active) return;

      try {
        es = new EventSource(jobEventsUrl(jobId, token));
      } catch {
        degrade();
        return;
      }
      setStreaming(true);

      es.onmessage = (ev: MessageEvent) => {
        if (!active) return;
        let data: StreamEvent;
        try {
          data = JSON.parse(ev.data) as StreamEvent;
        } catch {
          return; // ignore keep-alives / malformed frames
        }
        switch (data.type) {
          case "progress":
            if (data.state) {
              setJob((current) =>
                current ? { ...current, state: data.state! } : current
              );
            }
            if (typeof data.pct === "number") setPct(data.pct);
            if (data.message) setMessage(data.message);
            break;
          case "partial":
            setPartials((prev) => [
              ...prev,
              { index: data.index, dataUri: `data:${data.mime};base64,${data.b64}` },
            ]);
            break;
          case "completed":
            es?.close();
            es = null;
            setStreaming(false);
            // Pull final assets + presign-able keys. If that refetch fails
            // (proxy blip, expired token) the job state would never advance:
            // the stream is already closed and polling is off, so the card
            // spins forever AND the Generate button stays disabled. Degrade to
            // polling instead — the same recovery the `failed` branch uses.
            void refetchJob().then((fresh) => {
              if (!active) return;
              if (fresh == null) degrade();
            });
            break;
          case "failed": {
            // Narrow the frame's error before entering the async callback.
            const failureMessage =
              typeof data.error === "string"
                ? data.error
                : data.error?.message ?? "Generation failed";
            es?.close();
            es = null;
            setStreaming(false);
            // A `failed` frame is ALSO the server's stream fail-safe
            // (error.code === "stream_timeout") fired at MAX_STREAM_DURATION for a
            // job that is still running. Only treat it as terminal once the
            // refetched job actually reached a terminal state; otherwise the job
            // lives on, so degrade to polling instead of abandoning the card.
            void refetchJob().then((fresh) => {
              if (!active) return;
              const terminal =
                fresh != null &&
                ["completed", "failed", "cancelled"].includes(fresh.state);
              if (terminal) {
                setError(failureMessage); // genuine failure -> surface it
              } else {
                degrade(); // fail-safe / unconfirmed -> keep tracking via polling
              }
            });
            break;
          }
        }
      };

      es.onerror = () => {
        if (!active || !es) return;
        // Browsers auto-reconnect on transient drops (readyState CONNECTING).
        // Only degrade when the stream is permanently closed (e.g. bad/expired
        // token → non-2xx) and we never reached a terminal event.
        if (es.readyState === EventSource.CLOSED) degrade();
      };
    })();

    return () => {
      active = false;
      es?.close();
      es = null;
    };
  }, [jobId, refetchJob]);

  return {
    job: fallback ? polledJob : job,
    pct,
    message,
    partials,
    streaming,
    // Surface a polling give-up as a normal error so the card can render it
    // instead of spinning indefinitely.
    error: error ?? pollError?.message ?? null,
  };
}
